import { useCallback, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { track } from "../../lib/experiment";
import { purchasePackage, restorePurchases } from "../../lib/iap";
import { restore as storeKitDirectRestore } from "../../lib/storekitDirect";
import { applyTransactions } from "../../lib/iapAutoSync";
import { isIOS } from "../../lib/platform";

/**
 * Encapsulates ALL paywall purchase pathways:
 *   - `buyIOS(pkg)`     — StoreKit purchase via RevenueCat (iOS).
 *   - `buy(packageId)`  — Stripe Checkout redirect (web).
 *   - `onRestore()`     — RevenueCat restorePurchases (iOS, App-Store-required).
 *   - `onStartTrial()`  — Backend `/auth/start-trial` (any platform).
 *
 * Keeps `busy` state so a single in-flight purchase disables all buttons.
 * Returns the variant-aware `track()` events the original Paywall fired —
 * no analytics regression.
 *
 * IMPORTANT: This hook is iOS-aware via the `ios` flag passed in by the
 * caller. We deliberately error out (toast.error) if `buy(...)` is called on
 * iOS — Apple Guideline 3.1.1 prohibits linking to non-IAP payment surfaces.
 */
export function usePaywallPurchase({ user, signIn, startTrial, refresh, onClose, ios, variant }) {
  const [busy, setBusy] = useState(null);

  const buyIOS = useCallback(async (pkg) => {
    if (!user) {
      track("paywall_buy_click_anon", { package_id: pkg.identifier, platform: "ios" }, variant);
      signIn();
      return;
    }
    track("paywall_buy_click", { package_id: pkg.identifier, plan: user.plan, platform: "ios" }, variant);
    setBusy(pkg.identifier);
    try {
      const result = await purchasePackage(pkg);
      if (result.entitled) {
        toast.success("Welcome to Pro! 🎉");
        // Trigger backend sync so user.plan flips immediately. RevenueCat
        // webhook will also confirm server-side asynchronously.
        try {
          await api.post("/iap/sync");
        } catch (err) {
          if (process.env.NODE_ENV !== "production") {
            console.warn("iap/sync failed:", err?.message || err);
          }
        }
        // T49.X (Build 83) — Also walk Transaction.currentEntitlements
        // and POST via /iap/apple/apply. Belt-and-suspenders for the
        // "purchase succeeded but UI still shows Free Plan" bug:
        // /iap/sync only covers RC-recorded entitlements, but a
        // StoreKitDirect purchase never touches RC. Falling through
        // to the Apple-apply endpoint guarantees the entitlement
        // lands in Mongo regardless of which purchase path ran.
        try {
          const { syncAppleEntitlement } = await import("../../lib/iapAutoSync");
          await syncAppleEntitlement();
        } catch (_e) { /* non-fatal — /iap/sync already fired above */ }
        await refresh();
        track("paywall_purchase_success", { package_id: pkg.identifier, platform: "ios" }, variant);
        onClose();
      }
    } catch (e) {
      const code = e?.code || e?.errorCode;
      // T49.X — Treat "already subscribed" cancellations as a signal
      // to reconcile, not just close. Users hitting the yearly card
      // after silently upgrading were seeing Free Plan until they
      // tapped Monthly (which surfaced Apple's "you're already
      // subscribed" modal). Now we reconcile on cancel too — a
      // silent no-op when there's no entitlement, a full plan flip
      // when there is.
      if (code === "PURCHASE_CANCELLED" || /cancel/i.test(e?.message || "")) {
        track("paywall_purchase_cancelled", { package_id: pkg.identifier }, variant);
        try {
          const { syncAppleEntitlement } = await import("../../lib/iapAutoSync");
          const synced = await syncAppleEntitlement();
          if (synced?.active) {
            toast.success("Welcome to Pro! 🎉");
            await refresh();
            onClose();
          }
        } catch (_e) { /* silent — cancel path stays quiet */ }
      } else {
        track("paywall_buy_failed", { package_id: pkg.identifier, platform: "ios", err: code }, variant);
        toast.error(e?.message || "Purchase failed");
      }
    } finally {
      setBusy(null);
    }
  }, [user, signIn, refresh, onClose, variant]);

  const onRestore = useCallback(async () => {
    setBusy("restore");
    // T43 — On iOS, restore via the native StoreKit 2 Capacitor plugin
    // (StoreKitDirect.restore) instead of RevenueCat. Same rationale as
    // the purchase tap path: RC's `Purchases.configure()` wedges in the
    // TestFlight sandbox, which silently blocks RC's restorePurchases()
    // too — leaving the button visibly unresponsive. The native call
    // walks `Transaction.currentEntitlements` so it ALWAYS resolves
    // with either an empty array (no prior purchases) or the list of
    // active transactions, and the toast surfaces that result.
    try {
      if (isIOS()) {
        track("paywall_restore_click", { platform: "ios" }, variant);
        const result = await storeKitDirectRestore();
        const count = Array.isArray(result?.restored) ? result.restored.length : 0;
        if (count > 0) {
          // T47 — Push the native StoreKit transactions straight to the
          // dedicated Apple-apply endpoint (RC has no record of these,
          // bypassed). `applyTransactions` swallows network errors so a
          // failed POST never blocks the success toast.
          await applyTransactions(result.restored);
          await refresh();
          track("paywall_restore_success", { count, platform: "ios" }, variant);
          toast.success(`Subscription restored 🎉 (${count} purchase${count > 1 ? "s" : ""})`);
          onClose();
        } else {
          track("paywall_restore_empty", { platform: "ios" }, variant);
          toast.message("No previous purchases found for this Apple ID");
        }
        return;
      }
      // Web fallback — still goes through RC for parity with the
      // legacy implementation. Web hasn't been observed wedging.
      const result = await restorePurchases();
      if (result.entitled) {
        try {
          await api.post("/iap/sync");
        } catch (err) {
          if (process.env.NODE_ENV !== "production") {
            console.warn("iap/sync failed:", err?.message || err);
          }
        }
        await refresh();
        toast.success("Subscription restored 🎉");
        onClose();
      } else {
        toast.message("No previous purchases found for this Apple ID");
      }
    } catch (e) {
      track("paywall_restore_failed", { code: e?.code, platform: isIOS() ? "ios" : "web" }, variant);
      // T44 — Always surface the full diagnostic on iOS during the
      // App Review iteration. The old `/testflight/i.test(navigator.userAgent)`
      // heuristic never matched (iOS WKWebView UA has no "TestFlight"
      // token), so users only saw the generic message even in sandbox.
      // After App Review passes we can flip this to a build-time flag.
      const code = e?.code || "RESTORE_FAILED";
      const data = e?.data && Object.keys(e.data).length ? ` ${JSON.stringify(e.data)}` : "";
      const detail = `${code} — ${e?.message || ""}${data}`;
      toast.error(detail);
    } finally {
      setBusy(null);
    }
  }, [refresh, onClose, variant]);

  const buy = useCallback(async (packageId, opts = {}) => {
    if (ios) {
      toast.error("Use the in-app purchase buttons");
      return;
    }
    if (!user) {
      track("paywall_buy_click_anon", { package_id: packageId }, variant);
      signIn();
      return;
    }
    track("paywall_buy_click", { package_id: packageId, plan: user.plan }, variant);
    setBusy(packageId);
    try {
      const r = await api.post("/payments/checkout/session", {
        package_id: packageId,
        origin_url: window.location.origin,
      });
      // Persist session_id for the return page (in case query string is stripped)
      try {
        localStorage.setItem("tiplingo_last_checkout", r.data.session_id);
      } catch (err) {
        if (process.env.NODE_ENV !== "production") {
          console.warn("could not persist checkout session_id:", err?.message || err);
        }
      }
      window.location.href = r.data.url;
    } catch (e) {
      track("paywall_buy_failed", { package_id: packageId, status: e?.response?.status }, variant);
      // Graceful fallback: if the caller passed a `paymentLink` and our
      // server-side checkout failed, send the user to the Stripe-hosted
      // Payment Link so they can still complete the purchase. Keeps
      // revenue moving even when our backend has a hiccup.
      if (opts.paymentLink) {
        toast.message("Opening secure checkout…");
        window.location.href = opts.paymentLink;
        return;
      }
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setBusy(null);
    }
  }, [ios, user, signIn, variant]);

  const onStartTrial = useCallback(async () => {
    if (!user) {
      track("paywall_trial_click_anon", {}, variant);
      signIn();
      return;
    }
    if (user.plan === "trial") {
      toast.message("Your trial is already active");
      return;
    }
    track("paywall_trial_click", {}, variant);
    try {
      await startTrial();
      track("paywall_trial_started", {}, variant);
      toast.success("7-day Pro trial started — enjoy!");
      await refresh();
      onClose();
    } catch (e) {
      // startTrial triggers signIn() on 401; otherwise surface the error.
      if (e?.response?.status !== 401) {
        track("paywall_trial_failed", { status: e?.response?.status }, variant);
        toast.error(e?.response?.data?.detail || "Couldn't start trial");
        if (process.env.NODE_ENV !== "production") {
          console.error("startTrial failed:", e);
        }
      }
    }
  }, [user, signIn, startTrial, refresh, onClose, variant]);

  return { busy, buyIOS, buy, onRestore, onStartTrial };
}
